/**
 * Memorized Form Context
 *
 * 儲存 註冊表單欄位資料
 * 可以再轉跳頁面回來後 保持原本的state
 */
import type { Consumer, Context, FC, ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

interface MemorizedFormProviderProps {
  children?: ReactNode;
  initializeData?: any;
}
interface MemorizedFormContext {
  memorizedData?: any;
  setMemorizedData: (data: any) => void;
  clearMemorizedData: () => void;
}
type CreateMemorizedFormContext = () => {
  MemorizedFormConsumer: Consumer<MemorizedFormContext>;
  MemorizedFormProvider: FC<MemorizedFormProviderProps>;
  useMemorizedForm: () => MemorizedFormContext;
};

const createFormProvider = (context: Context<MemorizedFormContext>) => {
  const FormProvider: FC<MemorizedFormProviderProps> = ({ children, initializeData }) => {
    const [memorizedData, setMemorizedData] = useState(initializeData);
    useEffect(() => {
      setMemorizedData(initializeData);
    }, [initializeData]);
    const clearMemorizedData = () => {
      setMemorizedData(undefined);
    };
    return (
      <context.Provider value={{ memorizedData, setMemorizedData, clearMemorizedData }}>
        {children}
      </context.Provider>
    );
  };

  return FormProvider;
};
export const createMemorizedFormContext: CreateMemorizedFormContext = () => {
  const context = createContext<MemorizedFormContext>({
    setMemorizedData: () => {},
    clearMemorizedData: () => {},
  });
  context.displayName = 'MemorizedFormContext';

  return {
    MemorizedFormConsumer: context.Consumer,
    MemorizedFormProvider: createFormProvider(context),
    useMemorizedForm: () => useContext(context),
  };
};
export const { MemorizedFormConsumer, MemorizedFormProvider, useMemorizedForm } =
  createMemorizedFormContext();
